import { App, Form, InputNumber, Spin } from "antd";
import { useEffect, useRef, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { CustomActionButtons, CustomModal } from "sipli-fleet-core/components";
import { CrossIcon } from "src/assets/svgs";
import {
  useMutationSaveDistributorLimitations,
  useQueryGetDistributorLimitations,
} from "src/queries/distributor.queries";
import {
  normalizeDistributorLimitationsResponse,
  type DistributorLimitations,
  type DistributorLimitationsResponse,
  type DistributorLimitUpdatePayload,
} from "src/services/distributorService";
import "./DistributorSettingsModal.css";

const CrossIconComponent = CrossIcon as unknown as ComponentType<{
  className?: string;
}>;

interface DistributorSettingsModalProps {
  open: boolean;
  distributorId?: string | number | null;
  distributorName?: string;
  onClose: () => void;
}

type DistributorLimitFormValues = DistributorLimitations;
type DistributorSettingsTab = "main_information" | "limit" | "white_label";

const settingsTabs: Array<{
  key: DistributorSettingsTab;
  labelKey: string;
  disabled?: boolean;
}> = [
  { key: "main_information", labelKey: "main_information", disabled: true },
  { key: "limit", labelKey: "limit" },
  { key: "white_label", labelKey: "white_label", disabled: true },
];

const limitationCards: Array<{
  name: keyof DistributorLimitFormValues;
  titleKey: string;
  labelKey: string;
}> = [
  {
    name: "maxAutomations",
    titleKey: "automation",
    labelKey: "creation_limit",
  },
  {
    name: "maxRecipients",
    titleKey: "recipient",
    labelKey: "creation_limit",
  },
  {
    name: "maxUsers",
    titleKey: "user",
    labelKey: "creation_limit",
  },
  {
    name: "maxNotifications",
    titleKey: "notification_settings",
    labelKey: "creation_limit",
  },
  {
    name: "maxInfoNotificationsPerMonth",
    titleKey: "notification",
    labelKey: "creation_limit_per_month",
  },
  {
    name: "maxDddDriverParallelExtractions",
    titleKey: "ddd_driver_parallel_extractions",
    labelKey: "parallel_extraction_limit",
  },
  {
    name: "maxDddVehicleParallelExtractions",
    titleKey: "ddd_vehicle_parallel_extractions",
    labelKey: "parallel_extraction_limit",
  },
];

const defaultValues: DistributorLimitFormValues = {
  maxAutomations: 70,
  maxNotifications: 70,
  maxUsers: 70,
  maxRecipients: 70,
  maxInfoNotificationsPerMonth: 70,
  maxDddDriverParallelExtractions: 5,
  maxDddVehicleParallelExtractions: 5,
};

const limitFieldNames = Object.keys(defaultValues) as Array<keyof DistributorLimitFormValues>;

const limitCodeByFieldName: Record<keyof DistributorLimitFormValues, string> = {
  maxAutomations: "AUTOMATIONS",
  maxRecipients: "RECIPIENTS",
  maxUsers: "USERS",
  maxNotifications: "NOTIFICATION_SETTINGS",
  maxInfoNotificationsPerMonth: "INFO_NOTIFICATIONS_PER_MONTH",
  maxDddDriverParallelExtractions: "DDD_DRIVER_PARALLEL_EXTRACTIONS",
  maxDddVehicleParallelExtractions: "DDD_VEHICLE_PARALLEL_EXTRACTIONS",
};

const getResponsePayload = (data: unknown): Partial<DistributorLimitations> | null => {
  const maybeAxiosData = data as {
    data?: { response?: DistributorLimitationsResponse };
    response?: DistributorLimitationsResponse;
  };

  return normalizeDistributorLimitationsResponse(
    maybeAxiosData?.data?.response ?? maybeAxiosData?.response ?? null
  );
};

const removeEmptyLimitValues = (
  values: Partial<DistributorLimitations> | null
): Partial<DistributorLimitations> =>
  Object.fromEntries(
    Object.entries(values ?? {}).filter(([, value]) => value !== null && value !== undefined)
  );

const normalizeLimitValue = (value: unknown): number | null => {
  if (value === "" || value === undefined || value === null) return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const buildPayload = (
  values: DistributorLimitFormValues,
  currentValues: Partial<DistributorLimitations> | null
): DistributorLimitUpdatePayload[] =>
  limitFieldNames.reduce((payload, fieldName) => {
    const nextValue = normalizeLimitValue(values[fieldName]);
    const currentValue = normalizeLimitValue(currentValues?.[fieldName]);

    if (nextValue !== currentValue) {
      payload.push({
        code: limitCodeByFieldName[fieldName],
        value: nextValue ?? 0,
      });
    }

    return payload;
  }, [] as DistributorLimitUpdatePayload[]);

const DistributorSettingsModal = ({
  open,
  distributorId,
  onClose,
}: DistributorSettingsModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<DistributorLimitFormValues>();
  const [activeTab, setActiveTab] = useState<DistributorSettingsTab>("limit");
  const initialLimitValuesRef = useRef<DistributorLimitFormValues | null>(null);

  const limitationsQuery = useQueryGetDistributorLimitations(distributorId, {
    enabled: open && activeTab === "limit" && Boolean(distributorId),
  });
  const saveLimitationsMutation = useMutationSaveDistributorLimitations();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      initialLimitValuesRef.current = null;
      setActiveTab("limit");
      return;
    }

    if (activeTab !== "limit") return;
    if (limitationsQuery.isLoading) return;

    const values = getResponsePayload(limitationsQuery.data);
    const initialValues = {
      ...defaultValues,
      ...removeEmptyLimitValues(values),
    };

    initialLimitValuesRef.current = initialValues;
    form.setFieldsValue(initialValues);
  }, [activeTab, form, limitationsQuery.data, limitationsQuery.isLoading, open]);

  const handleClose = () => {
    form.resetFields();
    initialLimitValuesRef.current = null;
    onClose();
  };

  const handleSave = async () => {
    if (activeTab !== "limit") {
      handleClose();
      return;
    }

    if (!distributorId) return;

    try {
      const values = await form.validateFields();
      const payload = buildPayload(values, initialLimitValuesRef.current);

      if (!payload.length) {
        handleClose();
        return;
      }

      await saveLimitationsMutation.mutateAsync({
        distributorId,
        payload,
      });
      message.success(t("distributor_limits_saved_successfully"));
      handleClose();
    } catch (error) {
      const responseMessage =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;

      message.error(responseMessage ?? t("error"));
    }
  };

  const numberRules = [
    { required: true, message: t("field_required") },
    {
      validator: (_: unknown, value: unknown) => {
        if (value === "" || value === undefined || value === null) return Promise.resolve();
        const numericValue = Number(value);
        return Number.isInteger(numericValue) && numericValue >= 0
          ? Promise.resolve()
          : Promise.reject(new Error(t("limit_must_be_zero_or_more")));
      },
    },
  ];

  return (
    <CustomModal
      open={open}
      width={950}
      footer={null}
      closable={false}
      centered
      destroyOnHidden
      className="distributor-settings-modal"
      rootClassName="distributor-settings-modal-root"
      styles={{
        body: { padding: 0 },
        content: { padding: 0 },
      }}
      onCancel={handleClose}
    >
      <div className="distributor-settings-shell">
        <div className="distributor-settings-header">
          <h2 className="distributor-settings-title">{t("sipli_distributor_settings")}</h2>

          <button
            type="button"
            className="distributor-settings-close"
            aria-label={t("close")}
            onClick={handleClose}
          >
            <CrossIconComponent className="h-[14px] w-[14px]" />
          </button>
        </div>

        <div className="distributor-settings-layout">
          <aside className="distributor-settings-sidebar">
            {settingsTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`distributor-settings-menu-item ${
                  activeTab === tab.key ? "active" : ""
                }`}
                disabled={tab.disabled}
                aria-current={activeTab === tab.key ? "page" : undefined}
                onClick={() => setActiveTab(tab.key)}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </aside>

          <section className="distributor-settings-panel">
            {activeTab === "limit" ? (
              <>
                <h3 className="distributor-settings-section-title">{t("limit")}</h3>
                <p className="distributor-settings-section-copy">
                  {t("distributor_limits_recommendation")}
                </p>

                {limitationsQuery.isLoading ? (
                  <div className="distributor-settings-loader">
                    <Spin />
                  </div>
                ) : (
                  <Form
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                    className="distributor-settings-form"
                  >
                    {limitationCards.map((card) => (
                      <div className="distributor-limit-card" key={card.name}>
                        <h4>{t(card.titleKey)}</h4>
                        <Form.Item
                          name={card.name}
                          label={t(card.labelKey)}
                          rules={numberRules}
                          className="distributor-settings-field"
                        >
                          <InputNumber
                            min={0}
                            step={1}
                            placeholder={t("type")}
                            className="distributor-settings-input"
                          />
                        </Form.Item>
                      </div>
                    ))}
                  </Form>
                )}
              </>
            ) : (
              <div className="distributor-settings-empty-panel" />
            )}
          </section>
        </div>

        <CustomActionButtons
          className="distributor-settings-actions"
          onCancel={handleClose}
          onOk={() => void handleSave()}
          loading={limitationsQuery.isLoading || saveLimitationsMutation.isPending}
          cancelProps={{ className: "distributor-settings-action-button" }}
          okProps={{ className: "distributor-settings-action-button" }}
          okBtnLabel="save"
        />
      </div>
    </CustomModal>
  );
};

export default DistributorSettingsModal;
